import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = [".open", "title", "subtitle"]
const _hoisted_2 = {
  id: "modal-container-element",
  class: "modal-content"
}
const _hoisted_3 = { columns: "2" }
const _hoisted_4 = ["value", "disabled"]
const _hoisted_5 = ["value", "disabled"]
const _hoisted_6 = ["value", "disabled"]
const _hoisted_7 = { columns: "1" }
const _hoisted_8 = ["value", "disabled"]
const _hoisted_9 = { slot: "footer" }
const _hoisted_10 = ["disabled"]

import { computed, ref } from 'vue';
import { useStore } from 'vuex';

import { EPINIO_TYPES } from '../../types';
import { epinioExceptionToErrorsArray } from '../../utils/errors';
import { validateKubernetesName } from '@shell/utils/validators/kubernetes-name';
import Banner from '@components/Banner/Banner.vue';
import EpinioBuilderImageModel from '../../models/builderimages';


export default /*@__PURE__*/_defineComponent({
  __name: 'ImageModal',
  setup(__props, { expose: __expose }) {

const store = useStore() as any;
const t = store.getters['i18n/t'];

// Modal open state
const showModal = ref(false);
const modalMode = ref<'create' | 'edit' | 'view'>('create');

const initialValues = ref<EpinioBuilderImageModel | null>(null);

// Form fields (separate from the model to avoid proxy mutation issues)
const imageName = ref('');
const imageShortDescription = ref('');
const imageDescription = ref('');
const builderImage = ref('');

const saving = ref(false);
const errors = ref<string[]>([]);
const hasAssociatedApps = ref<boolean>(false);

const isEdit = computed(() => modalMode.value === 'edit');
const isView = computed(() => modalMode.value === 'view');

const isDirty = computed(() => {
  if (!initialValues.value) {
    return imageName.value !== '' ||
      imageShortDescription.value !== '' ||
      imageDescription.value !== '' ||
      builderImage.value !== '';
  }

  const isDirty = imageName.value !== (initialValues.value!.meta.name || '') ||
    imageShortDescription.value !== (initialValues.value!.short_description || '') ||
    imageDescription.value !== (initialValues.value!.description || '') ||
    builderImage.value !== (initialValues.value!.image || '');

  return isDirty;
});

const showDiscardConfirm = ref(false);

const validationPassed = computed(() => {
  if (!imageName.value) return false;
  if (!imageShortDescription.value) return false;
  if (!imageDescription.value) return false;
  if (!builderImage.value) return false;

  const nameErrors = validateKubernetesName(imageName.value, '', store.getters, undefined, []);
  return nameErrors.length === 0;
});

const canSave = computed(() => {
  const dirty = isDirty.value;
  const valid = validationPassed.value;
  return dirty && valid && !saving.value;
});

function openCreate() {
  errors.value = [];
  modalMode.value = 'create';
  imageName.value = '';
  imageShortDescription.value = '';
  imageDescription.value = '';
  builderImage.value = '';
  showModal.value = true;
}

function openEdit(row: EpinioBuilderImageModel) {
  errors.value = [];
  modalMode.value = 'edit';
  initialValues.value = row;
  imageName.value = row.name || row.meta?.name || '';
  imageShortDescription.value = row.short_description || '';
  imageDescription.value = row.description || '';
  builderImage.value = row.image || '';
  hasAssociatedApps.value = !!row.bound_apps;
  showModal.value = true;
}

function handleModalClose() {
  if (isDirty.value) {
    showDiscardConfirm.value = true;
  } else {
    closeModal();
  }
}

function handleKeepEditing() {
  showDiscardConfirm.value = false;
}

function handleDiscard() {
  showDiscardConfirm.value = false;
  closeModal();
}

function closeModal() {
  // Clear form state before setting showModal = false so that when Lit fires
  // modal-close (which triggers handleModalClose), isDirty is already false
  imageName.value = '';
  imageShortDescription.value = '';
  imageDescription.value = '';
  builderImage.value = '';
  errors.value = [];
  showDiscardConfirm.value = false;
  showModal.value = false;
  initialValues.value = null;
  hasAssociatedApps.value = false;
}

async function onSubmit() {
  if (!validationPassed.value || !isDirty.value || saving.value) return;

  saving.value = true;
  errors.value = [];

  try {
    if (isEdit.value && initialValues.value) {
      const image = initialValues.value;

      image.description       = imageDescription.value;
      image.short_description = imageShortDescription.value;
      image.image             = builderImage.value;

      await image.update();
      store.dispatch('growl/success', {
        title:   t('epinio.growl.builderImages.update.success.title'),
        message: t('epinio.growl.builderImages.update.success.message', { name: imageName.value }),
      });
      closeModal();
      store.dispatch('epinio/findAll', { type: EPINIO_TYPES.BUILDER_IMAGE, opt: { force: true } }).catch(() => {});
    } else {
      const image = await store.dispatch('epinio/create', { type: EPINIO_TYPES.BUILDER_IMAGE });

      image.metadata          = { name: imageName.value };
      image.description       = imageDescription.value;
      image.short_description = imageShortDescription.value;
      image.image             = builderImage.value;

      await image.create();
      store.dispatch('growl/success', {
        title:   t('epinio.growl.builderImages.create.success.title'),
        message: t('epinio.growl.builderImages.create.success.message', { name: imageName.value }),
      });
      closeModal();
      store.dispatch('epinio/findAll', { type: EPINIO_TYPES.BUILDER_IMAGE, opt: { force: true } }).catch(() => {});
    }
  } catch (err: any) {
    errors.value = epinioExceptionToErrorsArray(err);
    store.dispatch('growl/error', {
      title: isEdit.value
        ? t('epinio.growl.builderImages.save.error.updateTitle')
        : t('epinio.growl.builderImages.save.error.createTitle'),
      message: t('epinio.growl.builderImages.save.error.message'),
    });
    console.error('Error saving image:', err);
  } finally {
    saving.value = false;
  }
}

__expose({ openCreate, openEdit });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("trailhand-modal", {
    ".open": showModal.value,
    ".dismissible": false,
    title: (isView.value || isEdit.value) ? initialValues.value.meta?.name || 'Builder Image' : 'Builder Image',
    subtitle: (isView.value || isEdit.value) ? '' : 'Create New',
    position: "top",
    onModalClose: handleModalClose
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("trailhand-form-card", null, [
        (hasAssociatedApps.value)
          ? (_openBlock(), _createBlock(Banner, {
              key: 0,
              color: "warning",
              label: "This image is currently associated with one or more applications. Editing it may cause issues for future rebuilds."
            }))
          : _createCommentVNode("", true),
        _cache[6] || (_cache[6] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_3, [
          _createElementVNode("trailhand-text-input", {
            value: imageName.value,
            label: "Name",
            placeholder: "A Unique Name",
            required: true,
            disabled: isView.value || isEdit.value,
            onTextInputChange: _cache[0] || (_cache[0] = (e) => { imageName.value = e.detail.value; })
          }, null, 40, _hoisted_4),
          _cache[5] || (_cache[5] = _createTextVNode()),
          _createElementVNode("trailhand-text-input", {
            value: imageShortDescription.value,
            label: "Short Description",
            placeholder: "A brief description",
            required: true,
            disabled: isView.value,
            onTextInputChange: _cache[1] || (_cache[1] = (e) => { imageShortDescription.value = e.detail.value; })
          }, null, 40, _hoisted_5)
        ]),
        _cache[7] || (_cache[7] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", null, [
          _createElementVNode("trailhand-text-area", {
            value: imageDescription.value,
            label: "Description",
            placeholder: "A detailed description",
            disabled: isView.value,
            required: "",
            onTextAreaChange: _cache[2] || (_cache[2] = (e) => { imageDescription.value = e.detail.value; })
          }, null, 40, _hoisted_6)
        ]),
        _cache[8] || (_cache[8] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_7, [
          _createElementVNode("trailhand-text-input", {
            value: builderImage.value,
            label: "Image",
            placeholder: "e.g. registry.example.com/builder:latest",
            disabled: isView.value,
            required: "",
            onTextInputChange: _cache[3] || (_cache[3] = (e) => { builderImage.value = e.detail.value; })
          }, null, 40, _hoisted_8)
        ])
      ]),
      _cache[9] || (_cache[9] = _createTextVNode()),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (err, i) => {
        return (_openBlock(), _createBlock(Banner, {
          key: i,
          color: "error",
          label: err
        }, null, 8, ["label"]))
      }), 128))
    ]),
    _cache[15] || (_cache[15] = _createTextVNode()),
    _createElementVNode("div", _hoisted_9, [
      (isView.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("trailhand-button", {
              variant: "secondary",
              class: "mr-10",
              onButtonClick: closeModal
            }, "\n          Close\n        ", 32),
            _cache[10] || (_cache[10] = _createTextVNode()),
            _createElementVNode("trailhand-button", {
              variant: "primary",
              onButtonClick: _cache[4] || (_cache[4] = ($event: any) => (modalMode.value = 'edit'))
            }, "\n          Edit Configuration\n        ", 32)
          ], 64))
        : (showDiscardConfirm.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _cache[11] || (_cache[11] = _createElementVNode("span", { class: "discard-message" }, "You have unsaved changes.", -1)),
              _cache[12] || (_cache[12] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "secondary",
                class: "mr-10",
                onButtonClick: handleKeepEditing
              }, "\n          Keep Editing\n        ", 32),
              _cache[13] || (_cache[13] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "destructive",
                onButtonClick: handleDiscard
              }, "\n          Discard\n        ", 32)
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createElementVNode("trailhand-button", {
                variant: "secondary",
                class: "mr-10",
                onButtonClick: handleModalClose
              }, "\n          Cancel\n        ", 32),
              _cache[14] || (_cache[14] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "primary",
                disabled: !canSave.value,
                onButtonClick: onSubmit
              }, _toDisplayString(saving.value ? (isEdit.value ? 'Saving...' : 'Creating...') : (isEdit.value ? _unref(t)('generic.save') : _unref(t)('generic.create'))), 41, _hoisted_10)
            ], 64))
    ])
  ], 40, _hoisted_1))
}
}

})